<template>
  <div class="ar">
    <div v-for="arch in archRegions" :key="arch.key" class="ar-arch">
      <div class="ar-arch-head">{{ arch.label }}</div>
      <div v-for="quad in arch.quadrants" :key="quad.key" class="ar-quad">
        <div class="ar-quad-head">{{ quad.label }}</div>
        <template v-for="(group, idx) in quad.groups">
          <div :key="quad.key + '-' + idx" class="ar-row" :class="{ 'ar-row-with-fee': showFeeColumn }">
            <div class="ar-row-treat">
              <div class="ar-treat-line">
                {{ procLabel(group.items[0]) }}<span
                  v-if="group.items.length > 1"
                  class="ar-treat-count"
                  :title="group.items.length + ' instances'"
                >&nbsp;×{{ group.items.length }}</span>
              </div>
            </div>
            <!-- One chip per tooth, and when a tooth has a photo the CHIP
                 ITSELF is the button with the camera inside it. The camera used
                 to be a sibling element, which meant it could wrap onto the next
                 line on its own — an orphaned icon reads as belonging to
                 nothing, and it was the first thing spotted on desktop. Keeping
                 tooth and camera in one element makes that structurally
                 impossible, and gives a much bigger tap target chairside. -->
            <div class="ar-row-teeth">
              <template v-for="(tooth, tIdx) in group.teeth">
                <button
                  v-if="hasPhoto(tooth)"
                  :key="quad.key + '-' + idx + '-tooth-' + tIdx + '-' + tooth"
                  type="button"
                  class="ar-tooth-chip ar-tooth-chip-photo"
                  :title="'View photo of tooth ' + tooth"
                  :aria-label="'View photo of tooth ' + tooth"
                  @click.stop="$emit('open-tooth-photos', tooth)"
                >{{ tooth }}<i class="fa fa-camera ar-chip-cam"></i></button>
                <span
                  v-else
                  :key="quad.key + '-' + idx + '-tooth-' + tIdx + '-' + tooth"
                  class="ar-tooth-chip"
                >{{ tooth }}</span>
              </template>
              <span v-if="group.teeth.length === 0" class="ar-tooth-none">—</span>
            </div>
            <div class="ar-row-reason">{{ procReason(group.items[0]) }}</div>
            <div v-if="showFeeColumn" class="ar-row-fee">
              <div v-if="layers.netFee" class="ar-fee-main">{{ formatFee(layers.membership && group.totals.discount > 0 ? group.totals.fee + group.totals.discount : group.totals.fee) }}</div>
              <div v-if="anySubLayerOn" class="ar-fee-layers">
                <div v-if="layers.standardFee && group.totals.ucr > 0 && Math.abs(group.totals.ucr - group.totals.fee) > 0.01" class="ar-fee-layer">
                  <span class="ar-fee-layer-label">UCR</span>
                  <span class="ar-fee-layer-amt">{{ formatFee(group.totals.ucr) }}</span>
                </div>
                <div v-if="layers.insuranceEstimate && group.totals.insurancePrimary > 0" class="ar-fee-layer">
                  <span class="ar-fee-layer-label">{{ group.totals.insuranceSecondary > 0 ? 'Primary ins.' : 'Ins. covers' }}</span>
                  <span class="ar-fee-layer-amt ar-fee-layer-credit">−{{ formatFee(group.totals.insurancePrimary) }}</span>
                </div>
                <div v-if="layers.insuranceEstimate && group.totals.insuranceSecondary > 0" class="ar-fee-layer">
                  <span class="ar-fee-layer-label">Secondary ins.</span>
                  <span class="ar-fee-layer-amt ar-fee-layer-credit">−{{ formatFee(group.totals.insuranceSecondary) }}</span>
                </div>
                <div v-if="layers.membership && group.totals.discount > 0" class="ar-fee-layer">
                  <span class="ar-fee-layer-label">Discount</span>
                  <span class="ar-fee-layer-amt ar-fee-layer-credit">−{{ formatFee(group.totals.discount) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-for="alt in altsForGroup(group)"
            :key="quad.key + '-' + idx + '-alt-' + alt.procNum"
            class="ar-row ar-row-alt"
            :class="{ 'ar-row-alt-swappable': altSwapEnabled, 'ar-row-with-fee': showFeeColumn }"
            :title="altSwapEnabled ? 'Tap to switch — this becomes the primary' : ''"
            :tabindex="altSwapEnabled ? 0 : -1"
            @click="altSwapEnabled && $emit('swap-alt', { primaryProcNum: group.items[0].procNum, altProcNum: alt.procNum })"
            @keydown.enter.prevent="altSwapEnabled && $emit('swap-alt', { primaryProcNum: group.items[0].procNum, altProcNum: alt.procNum })"
            @keydown.space.prevent="altSwapEnabled && $emit('swap-alt', { primaryProcNum: group.items[0].procNum, altProcNum: alt.procNum })"
          >
            <div class="ar-row-treat ar-row-alt-treat">
              <span class="ar-or-pill">or</span>
              {{ procLabel(alt) }}
              <i v-if="altSwapEnabled" class="fa fa-exchange ar-swap-hint"></i>
            </div>
            <div class="ar-row-teeth ar-row-alt-teeth">
              <span v-if="alt.toothNum" class="ar-tooth-chip ar-tooth-chip-alt">#{{ alt.toothNum }}</span>
              <span v-else class="ar-tooth-none">—</span>
            </div>
            <div class="ar-row-reason ar-row-alt-reason">{{ procReason(alt) }}</div>
            <div v-if="showFeeColumn" class="ar-row-fee ar-row-alt-fee">{{ layers.netFee ? formatFee(alt.fee) : '' }}</div>
          </div>
        </template>
      </div>
    </div>
    <div v-if="archRegions.length === 0" class="ar-empty">
      No procedures grouped by region.
    </div>
  </div>
</template>

<script>
import { sortByPriority } from '../../utils/procedureOrder.js';
import { insuranceSplit } from '../../utils/insuranceSplit.js';

const archForTooth = (toothNum) => {
  const n = parseInt(toothNum, 10);
  if (!n || isNaN(n)) return null;
  if (n >= 1 && n <= 16) return 'upper';
  if (n >= 17 && n <= 32) return 'lower';
  return null;
};

const quadrantForTooth = (toothNum) => {
  const n = parseInt(toothNum, 10);
  if (!n || isNaN(n)) return 'full';
  if (n >= 1 && n <= 8) return 'ur';
  if (n >= 9 && n <= 16) return 'ul';
  if (n >= 17 && n <= 24) return 'll';
  if (n >= 25 && n <= 32) return 'lr';
  return 'full';
};

const QUAD_NAMES = {
  ur: 'Upper Right',
  ul: 'Upper Left',
  ll: 'Lower Left',
  lr: 'Lower Right',
  full: 'Full Arch',
};

const QUAD_ORDER_BY_ARCH = {
  upper: ['ur', 'ul', 'full'],
  lower: ['ll', 'lr', 'full'],
};

export default {
  name: 'ArchRegionView',
  props: {
    procedures: { type: Array, default: () => [] },
    altsByPrimary: { type: Object, default: () => ({}) },
    labelFn: { type: Function, default: null },
    reasonFn: { type: Function, default: null },
    // Slice 9d — chairside swap mode (Present Mode opts in).
    altSwapEnabled: { type: Boolean, default: false },
    // Two-level pricing — hides the Fee column entirely when false. When
    // true and any of pricingLayers.* is on, breakdown sub-lines render
    // beneath each summed office-collectable.
    showPrices: { type: Boolean, default: true },
    pricingLayers: {
      type: Object,
      default: () => ({ netFee: true, standardFee: true, insuranceEstimate: true, membership: true }),
    },
    // Photo Attachments — { [toothNum]: true } for teeth with a photo on THIS
    // plan, built by the parent (utils/photoFilter.js). Parents pass {} when
    // the showPhotos toggle is off, so this component never sees the toggle.
    photoTeeth: { type: Object, default: () => ({}) },
  },
  computed: {
    layers() { return this.pricingLayers || {}; },
    anySubLayerOn() {
      const L = this.layers;
      return !!(L.standardFee || L.insuranceEstimate || L.membership);
    },
    // True when SOMETHING (net fee or any sub-layer) is on AND the view's
    // master toggle is on. Drives whether the Fee grid column is rendered.
    showFeeColumn() {
      const L = this.layers;
      return this.showPrices && !!(L.netFee || L.standardFee || L.insuranceEstimate || L.membership);
    },
    archRegions() {
      const archBuckets = { upper: {}, lower: {} };
      const noArchBucket = {};

      // Sort by OD priority FIRST so each quadrant bucket (which preserves
      // push order) ends up priority-ordered: numeric priorities first, then
      // custom-named, then uncategorized last.
      sortByPriority(this.procedures).forEach((p) => {
        const arch = archForTooth(p.toothNum);
        const quad = arch ? quadrantForTooth(p.toothNum) : 'full';
        const target = arch ? archBuckets[arch] : noArchBucket;
        if (!target[quad]) target[quad] = [];
        target[quad].push(p);
      });

      const procHasAlts = (proc) => {
        const list = this.altsByPrimary[proc.procNum];
        return Array.isArray(list) && list.length > 0;
      };

      const newRow = (it) => ({
        items: [it],
        teeth: it.toothNum ? [`#${it.toothNum}`] : [],
        _teethSeen: new Set(it.toothNum ? [`#${it.toothNum}`] : []),
      });

      const buildArch = (label, key, bucket) => {
        const quadKeys = (QUAD_ORDER_BY_ARCH[key] || ['full']).filter((qk) => bucket[qk] && bucket[qk].length > 0);
        const quadrants = quadKeys.map((qk) => {
          const items = bucket[qk];
          // Within a quadrant, group procedures sharing the same procCode into a
          // single row (teeth chips accumulate). Procedures that have alts are
          // kept as their own row so the "or"-alternatives below can still
          // attach to a single primary unambiguously.
          const groups = [];
          const groupByProcCode = new Map();
          items.forEach((it) => {
            if (procHasAlts(it)) {
              groups.push(newRow(it));
              return;
            }
            const groupKey = it.procCode || `none-${it.procNum}`;
            if (!groupByProcCode.has(groupKey)) {
              const row = newRow(it);
              groups.push(row);
              groupByProcCode.set(groupKey, row);
            } else {
              const row = groupByProcCode.get(groupKey);
              row.items.push(it);
              if (it.toothNum) {
                const chip = `#${it.toothNum}`;
                if (!row._teethSeen.has(chip)) {
                  row._teethSeen.add(chip);
                  row.teeth.push(chip);
                }
              }
            }
          });
          // Sort teeth chips numerically + compute group totals.
          groups.forEach((g) => {
            g.teeth.sort((a, b) => {
              const an = parseInt(a.replace('#', ''), 10);
              const bn = parseInt(b.replace('#', ''), 10);
              return an - bn;
            });
            // Per-group totals for the fee column. Insurance contribution
            // splits into primary/secondary via the shared helper (explicit
            // backend fields when present, else the combined max(0, net −
            // patientFee) fallback on primary). `insurance` stays the combined
            // figure so single-insurance groups render exactly as before.
            let fee = 0;
            let ucr = 0;
            let insurancePrimary = 0;
            let insuranceSecondary = 0;
            let discount = 0;
            g.items.forEach((it) => {
              const net = parseFloat(it.fee) || 0;
              const procFee = parseFloat(it.procFee) || 0;
              const ins = insuranceSplit(it);
              fee += net;
              ucr += procFee;
              insurancePrimary += ins.primary;
              insuranceSecondary += ins.secondary;
              discount += parseFloat(it.discount) || 0;
            });
            g.totals = {
              fee,
              ucr,
              insurance: insurancePrimary + insuranceSecondary,
              insurancePrimary,
              insuranceSecondary,
              discount,
            };
            delete g._teethSeen;
          });
          return {
            key: qk,
            label: QUAD_NAMES[qk] || qk,
            groups,
          };
        });
        return { key, label, quadrants };
      };

      const out = [];
      if (Object.keys(archBuckets.upper).some((k) => archBuckets.upper[k].length > 0)) {
        out.push(buildArch('Upper Arch', 'upper', archBuckets.upper));
      }
      if (Object.keys(archBuckets.lower).some((k) => archBuckets.lower[k].length > 0)) {
        out.push(buildArch('Lower Arch', 'lower', archBuckets.lower));
      }
      if (Object.keys(noArchBucket).some((k) => noArchBucket[k].length > 0)) {
        out.push(buildArch('Full Arch', 'other', noArchBucket));
      }
      return out;
    },
  },
  methods: {
    /**
     * Does this chip's tooth have a photo on this plan?
     *
     * `group.teeth` holds already-formatted strings ("#14"), which is what the
     * chip renders, so strip the # back off before looking up. Kept as a
     * method rather than pre-computing on the group so the archRegions
     * computed stays about geometry and nothing else.
     */
    hasPhoto(toothLabel) {
      const n = parseInt(String(toothLabel || '').replace('#', ''), 10);
      return Number.isFinite(n) && !!this.photoTeeth[n];
    },
    altsForGroup(group) {
      // A "group" here is a collection of procedures sharing a tooth signature.
      // Alts attach to the first procedure of the group (its primary).
      if (!group || !group.items || group.items.length === 0) return [];
      const primaryProcNum = group.items[0].procNum;
      return this.altsByPrimary[primaryProcNum] || [];
    },
    procLabel(p) {
      if (!p) return '—';
      if (this.labelFn) return this.labelFn(p);
      return p.abbrDesc || p.descript || p.procCode || '—';
    },
    procReason(p) {
      if (!p) return '';
      if (this.reasonFn) {
        const r = this.reasonFn(p);
        if (r) return r;
      }
      if (p.descript && p.descript !== p.abbrDesc) return p.descript;
      return '';
    },
    formatFee(n) {
      if (n === null || n === undefined || n === 0) return '$0';
      const num = parseFloat(n) || 0;
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.ar {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  color: #334155;
}
.ar-arch { margin-bottom: 22px; }
.ar-arch-head {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 16px;
  font-weight: 500;
  color: #0d5f54;
  border-bottom: 1px solid #e4e7ec;
  padding-bottom: 6px;
  margin-bottom: 14px;
}

.ar-quad { margin-bottom: 16px; }
.ar-quad-head {
  font-size: 10.5px;
  color: #0d5f54;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 6px;
}

.ar-row {
  display: grid;
  grid-template-columns: 1.1fr 90px 1.6fr;
  column-gap: 16px;
  padding: 10px 0;
  border-bottom: 1px dashed #e4e7ec;
  font-size: 12.5px;
  align-items: start;
}
.ar-row-with-fee {
  grid-template-columns: 1fr 90px 1.4fr 90px;
}

.ar-row-treat { line-height: 1.55; }
.ar-treat-line {
  color: #1e293b;
  font-weight: 550;
  margin-bottom: 1px;
}
.ar-treat-count {
  color: #64748b;
  font-weight: 500;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
}

/* Flex rather than inline flow: every chip then sits on one shared baseline
   with an even gap, instead of each carrying its own margins and drifting when
   a photo chip (taller, because of the icon) lands mid-row. */
.ar-row-teeth {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 3px;
  line-height: 1.7;
}
.ar-tooth-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  background: #e6f1ef;
  color: #0d5f54;
  border-radius: 3px;
  font-size: 10.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ar-tooth-none { color: #94a3b8; }

/* Photo Attachments — a tooth chip that carries a photo. Same pill, one shade
   stronger plus an inline camera, so "has a photo" reads at a glance without
   the row turning into a line of icons. It is a <button>, so reset the browser
   chrome the plain chips never needed. */
.ar-tooth-chip-photo {
  border: none;
  font-family: inherit;
  gap: 4px;
  padding: 1px 7px 1px 6px;
  background: #d3ebe6;
  cursor: pointer;
  transition: background 0.12s ease;
}
.ar-tooth-chip-photo:hover { background: #bfe2db; }
.ar-tooth-chip-photo:focus-visible {
  outline: 2px solid #17a2a2;
  outline-offset: 2px;
}
.ar-chip-cam {
  font-size: 9.5px;
  color: #17a2a2;
  /* Optically centered — the camera glyph sits high in its em box. */
  position: relative;
  top: 0.5px;
}

/* A camera you can't tap is noise on paper. The chip stays (it is the tooth
   number); only the icon goes. Lives here rather than in the host pages' print
   blocks because those are scoped and can't select into a child component. */
@media print {
  .ar-chip-cam { display: none !important; }
  .ar-tooth-chip-photo { background: #e6f1ef; padding: 1px 6px; }
}

.ar-row-reason {
  color: #64748b;
  font-size: 12px;
  line-height: 1.55;
}

.ar-row-fee {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #0d5f54;
  white-space: nowrap;
}
.ar-fee-main {
  font-weight: 600;
  font-size: 13px;
}
.ar-fee-layers {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 10.5px;
}
.ar-fee-layer {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  color: #94a3b8;
}
.ar-fee-layer-label {
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 9.5px;
}
.ar-fee-layer-amt { font-variant-numeric: tabular-nums; }
.ar-fee-layer-credit { color: #64748b; }
.ar-row-alt-fee { color: #475569; font-weight: 400; font-size: 11.5px; }

.ar-empty {
  padding: 28px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

/* Alt sub-rows */
.ar-row-alt {
  border-bottom: 1px dashed #f1f3f6;
  padding-top: 4px;
  padding-bottom: 8px;
}
.ar-row-alt-treat {
  color: #475569;
  font-weight: 500;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.ar-row-alt-reason {
  color: #94a3b8;
  font-size: 11.5px;
  font-style: italic;
}
.ar-tooth-chip-alt {
  background: #f1f3f6;
  color: #94a3b8;
}
.ar-or-pill {
  display: inline-block;
  padding: 1px 6px;
  background: #f1f3f6;
  border-radius: 999px;
  font-size: 9.5px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

/* Slice 9d — chairside swap-enabled state. Same affordance pattern as
   ItemizedView so the chairside motor memory transfers across lenses. */
.ar-row-alt-swappable {
  cursor: pointer;
  outline: none;
  transition: background 0.12s;
}
.ar-row-alt-swappable:hover,
.ar-row-alt-swappable:focus {
  background: #ecf5f3;
}
.ar-row-alt-swappable:focus {
  box-shadow: inset 3px 0 0 0 #0d9488;
}
.ar-swap-hint {
  margin-left: 8px;
  color: #94a3b8;
  font-size: 11px;
  transition: color 0.12s, transform 0.12s;
}
.ar-row-alt-swappable:hover .ar-swap-hint,
.ar-row-alt-swappable:focus .ar-swap-hint {
  color: #0d9488;
  transform: translateX(2px);
}

/* Print — keep each grouped row and each quadrant heading-with-first-row
   from splitting across a page break. */
@media print {
  .ar-row { break-inside: avoid; }
  .ar-quad-head { break-after: avoid; }
  .ar-arch-head { break-after: avoid; }
}
</style>
